import { Box, Button, Card, Stack, Typography } from '@mui/joy';
import { CheckCircle as CheckIcon, Phone as PhoneIcon } from '@phosphor-icons/react';
import type { PublicMailerOrganization } from '../../../types/directMail';
import { formatPhoneNumber, telHref } from './brand';
import type { BrandPalette } from './brand';

interface MailerClaimConfirmationProps {
  organization: PublicMailerOrganization;
  palette: BrandPalette;
  /** True when the request was already on file, so the copy doesn't repeat "thanks for reaching out". */
  alreadyOnFile: boolean;
}

export function MailerClaimConfirmation({
  organization,
  palette,
  alreadyOnFile,
}: MailerClaimConfirmationProps) {
  return (
    <Card variant="outlined" sx={{ borderColor: palette.border, boxShadow: 'sm', textAlign: 'center' }}>
      <Stack spacing={2} alignItems="center" sx={{ py: { xs: 2, sm: 3 } }}>
        <Box sx={{ color: palette.base, display: 'flex' }}>
          <CheckIcon size={64} weight="fill" />
        </Box>

        <Typography level="h3" sx={{ fontWeight: 800 }}>
          {alreadyOnFile ? "You're already on our list" : 'Thank you — we got it'}
        </Typography>

        <Typography level="body-lg" textColor="text.secondary" sx={{ maxWidth: 420 }}>
          {alreadyOnFile
            ? `Someone from ${organization.name} is already working on your estimate. Give us a call if you'd like an update today.`
            : `A member of the ${organization.name} team will reach out shortly to confirm the details and answer your questions.`}
        </Typography>

        {organization.phone && (
          <>
            <Typography level="body-sm" textColor="text.tertiary">
              Would rather talk now?
            </Typography>
            <Button
              component="a"
              href={telHref(organization.phone)}
              size="lg"
              startDecorator={<PhoneIcon size={22} weight="fill" />}
              sx={{
                minHeight: 56,
                px: 3,
                fontSize: '1.05rem',
                fontWeight: 700,
                bgcolor: palette.base,
                color: palette.onBase,
                '&:hover': { bgcolor: palette.base, filter: 'brightness(0.92)' },
              }}
            >
              {formatPhoneNumber(organization.phone)}
            </Button>
          </>
        )}
      </Stack>
    </Card>
  );
}
